import { useState, useEffect, useMemo, useCallback } from "react";

/**
 * Custom hook for virtual scrolling optimization
 * Only renders visible items to improve performance with large datasets
 */
export const useVirtualScroll = (
  items,
  itemHeight = 50,
  containerHeight = 400
) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [containerRef, setContainerRef] = useState(null);

  // Calculate visible range with buffer for smoother scrolling
  const visibleRange = useMemo(() => {
    if (!items.length || itemHeight <= 0) return { start: 0, end: 0 };

    // Calculate how many items fit in the container
    const itemsPerView = Math.ceil(containerHeight / itemHeight);

    // Add buffer items above and below for smoother scrolling
    const buffer = Math.max(2, Math.ceil(itemsPerView * 0.5)); // At least 2 items, or 50% of viewport

    // Calculate start index (with buffer)
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - buffer);

    // Calculate end index (with buffer)
    const endIndex = Math.min(
      startIndex + itemsPerView + buffer * 2, // Viewport + buffers
      items.length
    );

    return { start: startIndex, end: endIndex };
  }, [scrollTop, itemHeight, containerHeight, items.length]);

  // Get visible items
  const visibleItems = useMemo(() => {
    return items.slice(visibleRange.start, visibleRange.end);
  }, [items, visibleRange]);

  // Calculate total height for scrollbar
  const totalHeight = items.length * itemHeight;

  // Calculate offset for positioning
  const offsetY = visibleRange.start * itemHeight;

  // Handle scroll events
  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  /** Keep DOM + React state in sync (programmatic scrollTop does not fire scroll events). */
  const applyScrollTop = useCallback(
    (top) => {
      const t = Math.max(0, Number(top) || 0);
      setScrollTop(t);
      if (containerRef) {
        containerRef.scrollTop = t;
      }
    },
    [containerRef],
  );

  // Set up scroll listener
  useEffect(() => {
    const container = containerRef;
    if (!container) return;

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [containerRef, handleScroll]);

  return {
    visibleItems,
    totalHeight,
    offsetY,
    setContainerRef,
    visibleRange,
    handleScroll,
    applyScrollTop,
  };
};
